import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useCookies } from 'react-cookie'
import { DEFAULT_PAGE_SIZE } from '../../common/constants'
import { type RoleGroup } from '../../models/Role'
import { getInviteRoleGroups } from './InviteRoleGroups'
import { REQUESTED_SEAT_GROUPS, type RequestedGroupKey } from './requestedGroups'
import { DEFAULT_TEAM_CHIP, type TeamChipKey, isInviteChip, resolveTeamChip } from './teamChips'

export const TEAM_MEMBERS_PAGE_SIZE_COOKIE = 'teamMembersPageSize'
const ONE_YEAR_IN_SECONDS = 365 * 24 * 60 * 60

const requestedPageParam = (key: RequestedGroupKey) => `${key}Page`

export const resetPages = (params: URLSearchParams) => {
  params.delete('page')
  REQUESTED_SEAT_GROUPS.forEach((group) => params.delete(requestedPageParam(group.key)))
}

const positiveInt = (value: string | number | null | undefined) => {
  const parsed = Number(value)
  return !Number.isNaN(parsed) && parsed >= 1 ? Math.floor(parsed) : null
}

const signature = (ids: string[]) => JSON.stringify([...ids].sort())

const roleGroupNamesFromParam = (param: string | null): string[] => {
  if (!param) {
    return []
  }
  let parsed: unknown
  try {
    parsed = JSON.parse(param)
  } catch {
    return []
  }
  if (!Array.isArray(parsed)) {
    return []
  }
  const stored = parsed.filter((ids): ids is string[] => Array.isArray(ids)).map(signature)
  return getInviteRoleGroups()
    .filter((group) => stored.includes(signature(group.roleIds)))
    .map((group) => group.name)
}

export interface TeamListUrlState {
  chip: TeamChipKey
  page: number
  requestedPages: Record<RequestedGroupKey, number>
  perPage: number
  namePattern: string | null
  roleGroupNames: string[]
  roleGroupOptions: RoleGroup[]
  hasActiveFilters: boolean
  buySeatFor: string | null
  clearBuySeatFor: () => void
  setChip: (chip: TeamChipKey) => void
  setNamePattern: (value: string | null) => void
  setRoleGroupNames: (names: string[]) => void
  setPage: (page: number) => void
  setRequestedPage: (key: RequestedGroupKey, page: number) => void
  setPerPage: (perPage: number, page: number) => void
  clearFilters: () => void
}

export const useTeamListUrlState = (hasInviteScope: boolean): TeamListUrlState => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [cookies, setCookie] = useCookies([TEAM_MEMBERS_PAGE_SIZE_COOKIE])

  const buySeatFor = searchParams.get('buySeatFor')
  const chip = resolveTeamChip(buySeatFor ? 'requested' : searchParams.get('state'), hasInviteScope)
  const page = positiveInt(searchParams.get('page')) ?? 1
  const requestedPages = Object.fromEntries(
    REQUESTED_SEAT_GROUPS.map((group) => [group.key, positiveInt(searchParams.get(requestedPageParam(group.key))) ?? 1]),
  ) as Record<RequestedGroupKey, number>
  const perPage = positiveInt(searchParams.get('size')) ?? positiveInt(cookies[TEAM_MEMBERS_PAGE_SIZE_COOKIE]) ?? DEFAULT_PAGE_SIZE
  const namePattern = searchParams.get('name') || null

  const roleGroupOptions = useMemo<RoleGroup[]>(() => getInviteRoleGroups(isInviteChip(chip) ? { onlyInvitable: true } : {}), [chip])

  const storedRoleGroupNames = useMemo(() => roleGroupNamesFromParam(searchParams.get('roleGroups')), [searchParams])
  const roleGroupNames = useMemo(
    () => storedRoleGroupNames.filter((name) => roleGroupOptions.some((option) => option.name === name)),
    [storedRoleGroupNames, roleGroupOptions],
  )

  const update = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          mutate(prev)
          return prev
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )

  const setChip = useCallback(
    (next: TeamChipKey) => {
      update((params) => {
        if (next === DEFAULT_TEAM_CHIP) {
          params.delete('state')
        } else {
          params.set('state', next)
        }
        resetPages(params)
      })
    },
    [update],
  )

  const setNamePattern = useCallback(
    (value: string | null) => {
      update((params) => {
        if (value) {
          params.set('name', value)
        } else {
          params.delete('name')
        }
        resetPages(params)
      })
    },
    [update],
  )

  const setRoleGroupNames = useCallback(
    (names: string[]) => {
      update((params) => {
        if (names.length > 0) {
          params.set(
            'roleGroups',
            JSON.stringify(
              getInviteRoleGroups()
                .filter((group) => names.includes(group.name))
                .map((group) => group.roleIds),
            ),
          )
        } else {
          params.delete('roleGroups')
        }
        resetPages(params)
      })
    },
    [update],
  )

  const setPage = useCallback(
    (next: number) => {
      update((params) => (next > 1 ? params.set('page', String(next)) : params.delete('page')))
    },
    [update],
  )

  const setRequestedPage = useCallback(
    (key: RequestedGroupKey, next: number) => {
      update((params) => (next > 1 ? params.set(requestedPageParam(key), String(next)) : params.delete(requestedPageParam(key))))
    },
    [update],
  )

  const setPerPage = useCallback(
    (nextPerPage: number, nextPage: number) => {
      setCookie(TEAM_MEMBERS_PAGE_SIZE_COOKIE, String(nextPerPage), { path: '/', maxAge: ONE_YEAR_IN_SECONDS })
      update((params) => {
        if (nextPerPage !== DEFAULT_PAGE_SIZE) {
          params.set('size', String(nextPerPage))
        } else {
          params.delete('size')
        }
        resetPages(params)
        if (nextPage > 1) {
          params.set('page', String(nextPage))
        }
      })
    },
    [update, setCookie],
  )

  const clearBuySeatFor = useCallback(() => {
    update((params) => params.delete('buySeatFor'))
  }, [update])

  const clearFilters = useCallback(() => {
    update((params) => {
      params.delete('name')
      params.delete('roleGroups')
      resetPages(params)
    })
  }, [update])

  return {
    chip,
    page,
    requestedPages,
    perPage,
    namePattern,
    roleGroupNames,
    roleGroupOptions,
    hasActiveFilters: namePattern !== null || roleGroupNames.length > 0,
    buySeatFor,
    clearBuySeatFor,
    setChip,
    setNamePattern,
    setRoleGroupNames,
    setPage,
    setRequestedPage,
    setPerPage,
    clearFilters,
  }
}
